import { Directive, OnInit } from '@angular/core';
import { ComponentPermissionService, ComponentPermissionData } from '../../services/component-permission.service';

/**
 * 權限基礎元件
 *
 * 所有需要權限控制的元件都可以繼承此類別，自動獲得權限檢查功能
 *
 * @example
 * ```typescript
 * export class MyComponent extends PermissionBaseComponent implements OnInit {
 *   constructor(
 *     permissionService: ComponentPermissionService
 *   ) {
 *     super(permissionService);
 *   }
 *
 *   async ngOnInit(): Promise<void> {
 *     await this.loadPermissions('myComponent');
 *     // ... 其他初始化邏輯
 *   }
 * }
 * ```
 *
 * 在模板中使用：
 * ```html
 * <button *ngIf="hasPermission('create')">新增</button>
 * <button *ngIf="hasPermission('edit')">編輯</button>
 * <button *ngIf="hasAnyPermission(['edit', 'delete'])">操作</button>
 * ```
 */
@Directive()
export abstract class PermissionBaseComponent {

  // === 權限相關屬性 ===
  componentPermission: ComponentPermissionData | null = null;
  permissionLoading: boolean = true;

  constructor(
    protected permissionService: ComponentPermissionService
  ) {}

  /**
   * 載入元件權限
   *
   * @param componentName 元件名稱（例如：'sdsListComponent'）
   */
  async loadPermissions(componentName: string): Promise<void> {
    try {
      this.permissionLoading = true;
      this.componentPermission = await this.permissionService.getPermission(componentName);

      if (this.componentPermission) {
        console.log(`[${componentName}] 權限已載入:`, this.componentPermission);
      } else {
        console.warn(`[${componentName}] 無法取得權限，將使用預設權限`);
      }
    } catch (error) {
      console.error(`[${componentName}] 載入權限時發生錯誤:`, error);
    } finally {
      this.permissionLoading = false;
    }
  }

  /**
   * 檢查是否有特定權限
   *
   * @param permission 權限代碼（例如：'create', 'edit', 'delete'）
   * @returns 是否有該權限
   */
  hasPermission(permission: string): boolean {
    return this.permissionService.hasPermission(this.componentPermission, permission);
  }

  /**
   * 檢查是否有任一權限
   *
   * @param permissions 權限代碼列表
   * @returns 是否有任一權限
   */
  hasAnyPermission(permissions: string[]): boolean {
    return this.permissionService.hasAnyPermission(this.componentPermission, permissions);
  }

  /**
   * 檢查是否有所有權限
   *
   * @param permissions 權限代碼列表
   * @returns 是否有所有權限
   */
  hasAllPermissions(permissions: string[]): boolean {
    return this.permissionService.hasAllPermissions(this.componentPermission, permissions);
  }

  /**
   * 取得權限的中文說明
   *
   * @param permissionCode 權限代碼
   * @returns 中文說明
   */
  getPermissionLabel(permissionCode: string): string {
    return this.permissionService.getPermissionLabel(permissionCode);
  }

  /**
   * 取得資料範圍的中文說明
   *
   * @returns 中文說明
   */
  getDataScopeLabel(): string {
    return this.permissionService.getDataScopeLabel(this.componentPermission?.dataScope);
  }
}
